/* WORLDS (owner call 2026-10-01): a group's channels sorted into subject
   matters the whole group shares — each the group's `workspace` node, ink's
   notebooks' own record. A world is a LENS, not a container (the dictionary's
   grammar): a channel stands in every world it lists (`world_ids`) and in none
   is Unsorted. Two rooms need no record: ALL CHANNELS (the master, no lens) and
   UNSORTED (so no channel is ever lost). The Mac's Worlds.swift says the same. */
import { AtriumOrder } from '@ofself/shell/atriumOrder'

export const ALL = '__all__'
export const UNSORTED = '__unsorted__'

export const worldName = (id, worlds = []) =>
  id === UNSORTED ? 'Unsorted' : worlds.find((w) => w.id === id)?.name || 'All channels'

/** Does `channel` stand in world `id`? A world gone from the group counts as none. */
export function inWorld(channel, id, worlds = []) {
  if (!id || id === ALL) return true
  const live = (channel?.world_ids || []).filter((w) => worlds.some((x) => x.id === w))
  return id === UNSORTED ? live.length === 0 : live.includes(id)
}

/* THE GROUP'S ORDER (2026-10-03): the worlds stand in the order the group
   dragged them into, the same on every device: each world's `order`, a
   fractional rank on its node (the shell's atrium order math); the unranked
   after, in founding order, so a new world lands last without a write. */
export const ordered = (worlds = []) => AtriumOrder.sorted(worlds)

/** The rooms of a group's atrium, in order: Unsorted (kept, never ranked,
    so it holds the head of the table), then every world in the group's order. */
export const worldRooms = (worlds = []) => [UNSORTED, ...ordered(worlds).map((w) => w.id)]

/* MOVING CHANNELS BETWEEN WORLDS (2026-10-03, the shell's select many → Move
   to…; owner call 2026-10-04: every tag app's three verbs, MOVE, ADD TO and
   REMOVE FROM). A world is a lens, so a move means, by where it starts (the
   dictionary's tags, the same grammar):
     · from a world:          the channel LEAVES it and JOINS the target; every
                              other world it stands in keeps it
     · from All or Unsorted:  it JOINS the target; nothing is left
     · to Unsorted:           it leaves every world (Unsorted is "in none")
     · to All channels:       it leaves the world it is moved from (REMOVE FROM)
     · an ADD (`keep`):       it JOINS the target and leaves nothing, from
                              anywhere; only a world can be joined
   Each channel's change is an OP, { id, add, remove }, applied by the server to
   what the channel holds at write time; its undo is the inverse op. A DM, or
   a channel of another group, never moves. The Mac's Worlds.plan says the same. */

const live = (channel, worlds) => (channel?.world_ids || []).filter((w) => worlds.some((x) => x.id === w))

/** The worlds after an op: removals first, then additions, no repeats. */
export function applyOp(ids, op) {
  const out = (ids || []).filter((w) => !(op.remove || []).includes(w))
  for (const w of op.add || []) if (!out.includes(w)) out.push(w)
  return out
}

/** The op that takes a channel back to `before` (its worlds), undoing `op`. */
export const inverseOp = (before, op) => ({
  id: op.id,
  add: (op.remove || []).filter((w) => before.includes(w)),
  remove: (op.add || []).filter((w) => !before.includes(w)),
})

export const isNoop = (op) => !op.add?.length && !op.remove?.length

/** A move planned over the channels as this client holds them: { ops, unchanged, skipped }. */
export function planMove(channels, ids, { to, from = ALL, worlds = [], keep = false }) {
  if (to === from) throw new Error(`these channels are already in ${worldName(to, worlds)}`)
  if (keep && (to === ALL || to === UNSORTED)) throw new Error(`a channel cannot be added to ${worldName(to, worlds)}`)
  const byId = new Map((channels || []).map((c) => [c.id, c]))
  const ops = [], unchanged = [], skipped = []
  for (const id of new Set(ids)) {
    const c = byId.get(id)
    if (!c || c.is_dm || !c.group_id) { skipped.push(id); continue }
    const has = live(c, worlds)
    const op = to === UNSORTED ? { id, add: [], remove: has }
      : to === ALL ? { id, add: [], remove: has.filter((w) => w === from) }
      : { id, add: has.includes(to) ? [] : [to], remove: keep ? [] : has.filter((w) => w === from) }
    if (isNoop(op)) unchanged.push(id); else ops.push(op)
  }
  return { ops, unchanged, skipped }
}
